/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Requirements page bootstrap, tab data loading, and deep-link navigation resolution.
 * @module logic/requirements/requirementsPageData
 */

import { logger, sanitizeError } from "../../utils/logger.js";
import { getUser } from "../user";
import {
  getUserRoleData,
  getActiveCategories,
  getSectors,
  getCertifications,
  getAllValidRequirements,
  getCompanyUnapprovedRequirements,
  getRequirementsFromSingleCompany,
  LOCALSTORAGE_USER_ID_KEY,
} from "./index.js";

const getUserId = () => localStorage.getItem(LOCALSTORAGE_USER_ID_KEY);

export const loadRequirementsReferenceData = async (navigate) => {
  const [sectorsData, certificationsData, categoriesData] = await Promise.all([
    getSectors(navigate),
    getCertifications(navigate),
    getActiveCategories(navigate),
  ]);

  return {
    sectors: Array.isArray(sectorsData) ? sectorsData : [],
    certifications: Array.isArray(certificationsData) ? certificationsData : [],
    categories: Array.isArray(categoriesData) ? categoriesData : [],
  };
};

export const loadRequirementsByTabIndex = async (tabIndex, navigate) => {
  const userId = getUserId();

  if (tabIndex === 0) {
    return getAllValidRequirements(userId, navigate);
  }
  if (tabIndex === 1) {
    return getRequirementsFromSingleCompany(userId, navigate);
  }
  if (tabIndex === 2) {
    return getCompanyUnapprovedRequirements(userId, navigate);
  }

  return null;
};

export const loadRequirementsPageData = async ({
  navigate,
  changePermissions,
}) => {
  await getUserRoleData(getUserId(), navigate, changePermissions);
  const referenceData = await loadRequirementsReferenceData(navigate);
  const requirements = await getAllValidRequirements(getUserId(), navigate);

  return {
    ...referenceData,
    requirements: Array.isArray(requirements) ? requirements : [],
  };
};

export const loadCurrentRequirementsUser = async (navigate) => {
  const userId = getUserId();
  if (!userId) {
    return null;
  }

  return getUser(userId, navigate);
};

export const resolveRequirementNavigationTarget = ({
  locationState,
  requirementsData,
}) => {
  if (locationState?.selectedRequirement && locationState?.openModal) {
    return {
      type: "selectedRequirement",
      requirement: locationState.selectedRequirement,
      currentUser: locationState.currentUser,
    };
  }

  if (
    locationState?.objectId !== undefined &&
    locationState?.objectId !== null
  ) {
    logger.info("location.state.objectId: ", {
      data: locationState.objectId,
    });

    if (!requirementsData?.length) {
      return { type: "pendingObjectId" };
    }

    const requirement = requirementsData.find(
      (req) => req._id === locationState.objectId,
    );

    logger.info("requirement found: ", { data: requirement });

    if (!requirement) {
      logger.error("Requirement not found");
      return { type: "notFound" };
    }

    return {
      type: "objectId",
      requirement,
    };
  }

  return { type: "none" };
};

export const logRequirementsPageDataError = (error) => {
  logger.error("Error", { err: sanitizeError(error) });
};
